import React, { useState, useEffect, useCallback } from 'react';

// Hotel Config → Lock Authorisations
// Names the auth slots the active vendor exposes (Onity has 8). Each slot
// gets a display name, an "default-granted" flag (auto-checked at check-in),
// and an "is-billable" flag (informational; revenue collection happens in
// folio, not here).
//
// Leaving a slot's display_name empty deletes the row — the slot stays
// available but appears as "Slot N — (unnamed)" in the encode modal.
export default function PmsLockAuthorizations({ token, selectedProperty }) {
  const [data, setData] = useState({ vendor: '', slot_count: 8, slots: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'
  }), [token]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/v1/lock/authorizations', { headers: headers() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load authorisations');
      setData(json);
    } catch (e) {
      setError(e.message || 'Load failed');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load, selectedProperty?.id]);

  const updateSlot = (n, patch) => {
    setData((d) => ({
      ...d,
      slots: d.slots.map((s) => s.auth_number === n ? { ...s, ...patch } : s)
    }));
  };

  const save = async () => {
    setSaving(true); setError(''); setSavedAt(null);
    try {
      const res = await fetch('/api/v1/lock/authorizations', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ slots: data.slots })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Save failed');
      setSavedAt(new Date());
      await load();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ margin: 0, color: '#1e293b' }}>Lock Authorisations</h2>
      <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: 4, marginBottom: 20 }}>
        Name the auth slots this property's lock vendor exposes ({data.vendor || 'pending'} · {data.slot_count} slots).
        Leave a name empty to remove it.
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={th}>#</th>
                <th style={th}>Display name</th>
                <th style={th}>Default granted</th>
                <th style={th}>Billable</th>
              </tr>
            </thead>
            <tbody>
              {data.slots.map((s) => (
                <tr key={s.auth_number} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ ...td, fontWeight: 600 }}>{s.auth_number}</td>
                  <td style={td}>
                    <input style={inp} value={s.display_name}
                      onChange={(e) => updateSlot(s.auth_number, { display_name: e.target.value })}
                      placeholder={`Slot ${s.auth_number} — e.g. Spa, Pool, Parking`} />
                  </td>
                  <td style={td}>
                    <input type="checkbox" checked={s.default_granted}
                      onChange={(e) => updateSlot(s.auth_number, { default_granted: e.target.checked })} />
                  </td>
                  <td style={td}>
                    <input type="checkbox" checked={s.is_billable}
                      onChange={(e) => updateSlot(s.auth_number, { is_billable: e.target.checked })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14, alignItems: 'center' }}>
            {savedAt && <span style={{ color: '#166534', fontSize: '0.85rem' }}>Saved at {savedAt.toLocaleTimeString()}</span>}
            <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, fontSize: '0.8rem', color: '#64748b' }}>
        These names appear in the Encode Card modal. Default-granted slots are pre-ticked at check-in. The billable flag is informational only — revenue collection runs through the existing folio flow.
      </div>
    </div>
  );
}

const th = { padding: '10px 12px', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '10px 12px', color: '#0f172a' };
const inp = { padding: '7px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const btnPrimary = { background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' };
